<script setup>
import { ref, watch, nextTick, computed, onMounted } from "vue";
import { Inertia } from "@inertiajs/inertia";
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import ConfirmationModal from "@/Components/ConfirmationModal.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import TextareaInput from "@/Components/TextareaInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { useHelpers } from "@/helpers";
import _ from "lodash";
import LookbookIndexFilters from "../../../Components/Sales/LookbookIndexFilters.vue";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import LoadingOverlay from "@/Components/ExportLoadingOverlay.vue";

const props = defineProps({
  elements: Object,
  filters: Object,

  clients: Array,
  departments: Array,
  garments: Array,
  fabrics: Array,
  colors: Array,
  covers: Object,
  coverLibrary: Array,
  totalElementsCount: Number,
});

const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();

const canManageLookbook = computed(
  () =>
    helpers.userHasPermission(currentUser, "can_view_lookbook") ||
    helpers.userHasPermission(currentUser, "admin")
);
const canAccessLookbook = computed(
  () =>
    helpers.userHasPermission(currentUser, "lookbook_read_only") ||
    canManageLookbook.value
);
const canDownloadLookbookPdf = computed(() => canAccessLookbook.value);
const canViewOnlyLookbook = computed(
  () => canAccessLookbook.value && !canManageLookbook.value
);
const canDeleteLookbookElements = computed(
  () => helpers.userHasPermission(currentUser, "admin")
);
const canSeeNotes = computed(() => canManageLookbook.value);

const exportingAllPdfOverlay = ref(false);
const exportingAllPdfButton = ref(false);

const page = usePage()

const logoUrl = computed(() => page.props.value.app?.logoSrc ?? null)
const branding = computed(() => page.props.value.app?.branding ?? {})
const brandDisplayName = computed(() => branding.value.displayName || 'Premier Uniforms, LLC')
const lookbookTitle = computed(() => branding.value.lookbookTitle || 'Uniforms Look Book')
const lookbookWebsite = computed(() => branding.value.lookbookWebsite || null)
const lookbookPhone = computed(() => branding.value.lookbookPhone || null)
const lookbookEmail = computed(() => branding.value.lookbookEmail || null)
const lookbookLocations = computed(() => branding.value.lookbookLocations || null)
const defaultCoverImageUrl = "/content/images/lookbook-cover-image.png";
const printWatermarkUrl = ref(logoUrl);
const coverDisclaimer =
  "The information contained in this electronic mail transmission is confidential and intended to be sent only to the stated recipient of the transmission. This transmission is copyright protected from unauthorized use or if you are not the intended recipient or the intended recipient's agent, you are hereby notified that any review, use, dissemination, distribution or copying of this communication is prohibited. We also ask you to notify us immediately by telephone and to delete this transmission with any attachments and destroy all copies in any form. Thank you for your cooperation.";

const defaultFilters = {
  project_name: null,
  department: null,
  garment: null,
  project_description: null,
  notes: null,
  fabric: null,
  color_id: null,
  client: null,
};

const searching = ref(false);
const showCoversSection = ref(false);

const selectedCoverStorageKey = "lookbook.selectedCoverId";

const loadSelectedCover = () => {
  try {
    const raw = localStorage.getItem(selectedCoverStorageKey);
    if (raw === null || raw === undefined || raw === "") return null;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const selectedCoverId = ref(loadSelectedCover());
const selectedCover = computed(() =>
  (props.coverLibrary || []).find((c) => Number(c.id) === Number(selectedCoverId.value)) || null
);
const currentIndexUrl = computed(() =>
  route("sales.lookbook.index", route().params || {})
);

const persistSelectedCover = () => {
  try {
    if (selectedCoverId.value === null || selectedCoverId.value === undefined) {
      localStorage.removeItem(selectedCoverStorageKey);
      return;
    }
    localStorage.setItem(selectedCoverStorageKey, String(selectedCoverId.value));
  } catch (e) {}
};

watch(selectedCoverId, () => persistSelectedCover());

watch(
  () => props.coverLibrary,
  () => {
    if (!selectedCoverId.value) return;
    const exists = (props.coverLibrary || []).some(
      (c) => Number(c.id) === Number(selectedCoverId.value)
    );
    if (!exists) {
      selectedCoverId.value = null;
      persistSelectedCover();
    }
  },
  { immediate: true }
);

const hasAnyFiltersInProps = computed(() => Object.keys(props.filters || {}).length > 0);
onMounted(() => {
  const hasPersistedFilters = Object.keys(compactFilters(persisted.filters || {})).length > 0;

  if (!hasAnyFiltersInProps.value && hasPersistedFilters) {
    executeSearchRequest();
  }

  void buildWhitePrintWatermark();
});

const loadPersisted = () => {
  try {
    const f = localStorage.getItem("lookbook.filters");
    return {
      filters: f ? JSON.parse(f) : null,
    };
  } catch (e) {
    return { filters: null };
  }
};

const persisted = loadPersisted();

const buildWhitePrintWatermark = async () => {
  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  try {
    const logoImage = await loadImage(logoUrl);
    const w = logoImage.naturalWidth || logoImage.width;
    const h = logoImage.naturalHeight || logoImage.height;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(logoImage, 0, 0, w, h);
    ctx.globalCompositeOperation = "source-in";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";

    printWatermarkUrl.value = canvas.toDataURL("image/png");
  } catch (e) {
    printWatermarkUrl.value = logoUrl;
  }
};

const filterValues = ref({
  ...defaultFilters,
  ...(props.filters || {}),
  ...(persisted.filters || {}),
});

if (!canSeeNotes.value) {
  filterValues.value.notes = null;
}

const compactFilters = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && String(v).trim() !== "") out[k] = v;
  });
  return out;
};

const executeSearchRequest = () => {
  searching.value = true;

  const queryParameters = route().params || {};
  if (queryParameters.page) delete queryParameters.page;
  if (queryParameters.covers_page) delete queryParameters.covers_page;

  queryParameters.filters = compactFilters(filterValues.value);
  if (!canSeeNotes.value) {
    delete queryParameters.filters.notes;
  }

  try {
    localStorage.setItem(
      "lookbook.filters",
      JSON.stringify(queryParameters.filters)
    );
  } catch (e) {}

  Inertia.get(route("sales.lookbook.index", queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    replace: true,
    onFinish: () => {
      searching.value = false;
    },
  });
};

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const resetFilters = () => {
  searching.value = true;
  filterValues.value = { ...defaultFilters };
  executeSearchRequest();
};

// -------------------- Preview modal --------------------
const showPreviewModal = ref(false);
const elementToPreview = ref(null);

const openPreview = (el) => {
  elementToPreview.value = el;
  showPreviewModal.value = true;
};
const closePreview = () => {
  showPreviewModal.value = false;
  elementToPreview.value = null;
};

const previewTitleLine = (el) => el?.project_name || "Lookbook Element";
const safe = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const joinInfoLine = (...parts) => parts.filter((p) => p && p !== "—").join(", ") || "—";

const garmentName = (el) =>
  el?.garment_model?.garmentName || (el?.garment ? `#${el.garment}` : "—");
const fabricName = (el) =>
  el?.fabric_model?.fabName || (el?.fabric ? `#${el.fabric}` : "—");
const colorName = (el) =>
  el?.color_model?.colorName || (el?.color_id ? `#${el.color_id}` : "—");
const primaryInfoLine = (el) => joinInfoLine(safe(el?.deptName), garmentName(el));
const secondaryInfoLine = (el) => joinInfoLine(fabricName(el), colorName(el));

const goEditFromPreview = () => {
  if (!canManageLookbook.value) return;
  if (!elementToPreview.value?.id) return;
  window.location.href = route(
    "sales.lookbook.edit",
    elementToPreview.value.id
  );
};

const copyElement = (el) => {
  if (!canManageLookbook.value) return;
  if (!el?.id) return;

  Inertia.get(
    route("sales.lookbook.create", { copyFrom: el.id }),
    {},
    {
      preserveScroll: true,
    }
  );
};

// -------------------- Covers --------------------
const showCoverPreviewModal = ref(false);
const coverToPreview = ref(null);
const coverPreviewItem = ref(null);
const exportingCoverPreviewPdf = ref(false);

const deleteCover = (cover) => {
  if (!canManageLookbook.value) return;
  if (!cover?.id) return;
  if (!window.confirm("Are you sure you want to delete this cover?")) return;

  Inertia.delete(route("sales.lookbook.covers.destroy", cover.id), {
    preserveScroll: true,
  });
};

const openCoverPreview = (cover) => {
  coverToPreview.value = cover;
  showCoverPreviewModal.value = true;
};

const closeCoverPreview = () => {
  coverToPreview.value = null;
  showCoverPreviewModal.value = false;
};

const formatCoverDate = (coverDate) => {
  if (!coverDate) return "—";
  return coverDate;
};

const formatElementUpdatedAt = (value) => {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const coverTitle = (cover) => {
  if (!cover) return "";
  return cover?.title || "Lookbook";
};

const buildCoverPdfFilename = (cover) => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;

  const base = (cover?.title || "lookbook_cover")
    .toString()
    .trim()
    .replace(/[^a-z0-9\-_]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return `${base}_${stamp}.pdf`;
};

// -------------------- Helpers for PDF sizing --------------------
const waitForImages = async (rootEl) => {
  if (!rootEl) return;
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) => {
      if (!img?.src) return Promise.resolve();
      // If the browser already marked the image as complete (loaded or failed),
      // do not wait again; this prevents hanging on repeated exports.
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          img.removeEventListener("load", done);
          img.removeEventListener("error", done);
          resolve();
        };

        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });

        // Safety timeout: if no event arrives, continue export instead of blocking forever.
        setTimeout(done, 3000);
      });
    })
  );
};

const waitForBrowserPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const fitPrintPages = (rootEl) => {
  if (!rootEl) return;

  const pages = Array.from(rootEl.querySelectorAll(".lookbook-page"));
  pages.forEach((page) => {
    const wrapper = page.querySelector(".print-wrapper");
    const title = page.querySelector(".print-title");
    const info = page.querySelector(".print-info");
    const imgWrap = page.querySelector(".print-image-wrapper");
    const img = page.querySelector(".print-image");

    if (!imgWrap) return;

    imgWrap.style.height = "";
    imgWrap.style.maxHeight = "";
    if (img) {
      img.style.width = "";
      img.style.maxWidth = "";
      img.style.maxHeight = "";
      img.style.height = "";
      img.style.objectFit = "";
      img.style.objectPosition = "";
    }

    if (title && info) {
      const titleRect = title.getBoundingClientRect();
      const infoRect = info.getBoundingClientRect();
      const wrapperStyles = wrapper ? window.getComputedStyle(wrapper) : null;
      const rowGap = Number.parseFloat(wrapperStyles?.rowGap || "0") || 0;
      const freeSpace = Math.max(
        0,
        Math.floor(infoRect.top - titleRect.bottom - rowGap * 2)
      );
      imgWrap.style.height = `${freeSpace}px`;
      imgWrap.style.maxHeight = `${freeSpace}px`;
    }

    if (img) {
      img.style.width = "auto";
      img.style.height = "auto";
      img.style.maxWidth = "100%";
      img.style.maxHeight = "100%";
      img.style.objectFit = "contain";
      img.style.objectPosition = "center";
    }
  });
};

// -------------------- Preview export PDF --------------------
const exportingPreviewPdf = ref(false);
const previewExportItem = ref(null);

const buildElementPdfFilename = (el) => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;

  const base = (el?.project_name || "lookbook_element")
    .toString()
    .trim()
    .replace(/[^a-z0-9\-_]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return `${base}_${stamp}.pdf`;
};

const downloadPdfFromPreview = async () => {
  const el = elementToPreview.value;
  if (!el?.id) return;

  const container = document.getElementById("lookbook-preview-print");
  if (!container) return;

  exportingPreviewPdf.value = true;

  try {
    previewExportItem.value = el;

    await nextTick();
    await nextTick();

    container.classList.add("exporting");
    await waitForBrowserPaint();
    await waitForImages(container);
    await waitForBrowserPaint();
    fitPrintPages(container);
    await waitForBrowserPaint();

    const pages = Array.from(container.querySelectorAll(".lookbook-page"));
    if (!pages.length) return;

    const pageWidthIn = 8.5;
    const pageHeightIn = 11;
    const marginIn = 0.5;
    const printWidthPx = 720;
    const printHeightPx = 960;

    const pdf = new jsPDF({
      unit: "in",
      format: "letter",
      orientation: "portrait",
    });

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: printWidthPx,
        height: printHeightPx,
        windowWidth: printWidthPx,
        windowHeight: printHeightPx,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      if (i > 0) pdf.addPage("letter", "portrait");

      pdf.addImage(
        imgData,
        "JPEG",
        marginIn,
        marginIn,
        pageWidthIn - marginIn * 2,
        pageHeightIn - marginIn * 2
      );
    }

    pdf.save(buildElementPdfFilename(el));
  } finally {
    const c = document.getElementById("lookbook-preview-print");
    if (c) c.classList.remove("exporting");
    exportingPreviewPdf.value = false;
  }
};

const downloadCoverPreviewPdf = async (cover) => {
  const item = cover || coverToPreview.value;
  if (!item?.id) return;

  const container = document.getElementById("lookbook-cover-preview-print");
  if (!container) return;

  exportingCoverPreviewPdf.value = true;

  try {
    coverPreviewItem.value = item;

    await nextTick();
    await nextTick();

    container.classList.add("exporting");
    await waitForBrowserPaint();
    await waitForImages(container);
    await waitForBrowserPaint();

    const page = container.querySelector(".lookbook-page");
    if (!page) return;

    const canvas = await html2canvas(page, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      width: 720,
      height: 960,
      windowWidth: 720,
      windowHeight: 960,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF({
      unit: "in",
      format: "letter",
      orientation: "portrait",
    });

    pdf.addImage(imgData, "JPEG", 0.5, 0.5, 7.5, 10);
    pdf.save(buildCoverPdfFilename(item));
  } finally {
    const c = document.getElementById("lookbook-cover-preview-print");
    if (c) c.classList.remove("exporting");
    exportingCoverPreviewPdf.value = false;
    coverPreviewItem.value = null;
  }
};

// Download image
const downloadImageFromPreview = async () => {
  const el = elementToPreview.value;
  if (!el?.url) return;

  try {
    const res = await fetch(el.url, { credentials: "include" });
    const blob = await res.blob();
    const ext = (el.file_name || "image").split(".").pop();
    const safeName = (el.project_name || "lookbook")
      .toString()
      .replace(/[^a-z0-9\-_]+/gi, "-");

    const triggerDownload = (downloadBlob) => {
      const objectUrl = URL.createObjectURL(downloadBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${safeName}-${el.id}.${ext || "png"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    };

    if (!canViewOnlyLookbook.value) {
      triggerDownload(blob);
      return;
    }

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    const sourceObjectUrl = URL.createObjectURL(blob);
    try {
      const [sourceImage, watermarkImage] = await Promise.all([
        loadImage(sourceObjectUrl),
        loadImage(logoUrl),
      ]);

      const width = sourceImage.naturalWidth || sourceImage.width;
      const height = sourceImage.naturalHeight || sourceImage.height;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        triggerDownload(blob);
        return;
      }

      ctx.drawImage(sourceImage, 0, 0, width, height);

      const maxWmWidth = width * 0.72;
      const maxWmHeight = height * 0.72;
      const wmScale = Math.min(
        maxWmWidth / (watermarkImage.naturalWidth || watermarkImage.width),
        maxWmHeight / (watermarkImage.naturalHeight || watermarkImage.height)
      );
      const wmWidth = (watermarkImage.naturalWidth || watermarkImage.width) * wmScale;
      const wmHeight = (watermarkImage.naturalHeight || watermarkImage.height) * wmScale;

      const watermarkCanvas = document.createElement("canvas");
      watermarkCanvas.width = Math.max(1, Math.round(wmWidth));
      watermarkCanvas.height = Math.max(1, Math.round(wmHeight));
      const watermarkCtx = watermarkCanvas.getContext("2d");

      if (watermarkCtx) {
        // Draw original logo, then tint it to pure white.
        watermarkCtx.drawImage(
          watermarkImage,
          0,
          0,
          watermarkCanvas.width,
          watermarkCanvas.height
        );
        watermarkCtx.globalCompositeOperation = "source-in";
        watermarkCtx.fillStyle = "#ffffff";
        watermarkCtx.fillRect(0, 0, watermarkCanvas.width, watermarkCanvas.height);
        watermarkCtx.globalCompositeOperation = "source-over";

        // Centered diagonal watermark, subtle transparency.
        ctx.save();
        ctx.translate(width / 2, height / 2);
        ctx.rotate((-30 * Math.PI) / 180);
        ctx.globalAlpha = 0.42;
        ctx.drawImage(
          watermarkCanvas,
          -watermarkCanvas.width / 2,
          -watermarkCanvas.height / 2
        );
        ctx.restore();
      }

      const outputType = blob.type?.startsWith("image/") ? blob.type : "image/png";
      const watermarkedBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, outputType, 0.95)
      );

      if (!watermarkedBlob) {
        triggerDownload(blob);
        return;
      }

      triggerDownload(watermarkedBlob);
    } finally {
      URL.revokeObjectURL(sourceObjectUrl);
    }
  } catch (e) {
    window.open(el.url, "_blank");
  }
};

// -------------------- Delete modal --------------------
const showDeleteModal = ref(false);
const elementToDelete = ref(null);

const confirmDelete = (el) => {
  if (!canDeleteLookbookElements.value) return;
  elementToDelete.value = el;
  showDeleteModal.value = true;
};
const closeDeleteModal = () => {
  showDeleteModal.value = false;
  elementToDelete.value = null;
};
const deleteElement = () => {
  if (!canDeleteLookbookElements.value) return;
  if (!elementToDelete.value) return;

  Inertia.delete(route("sales.lookbook.destroy", elementToDelete.value.id), {
    preserveScroll: true,
    onFinish: () => closeDeleteModal(),
  });
};

// -------------------- Export ALL PDF --------------------
const exportItems = ref([]);
const selectionStorageKey = "lookbook.selectedIds";
const filteredIdsCacheStorageKey = "lookbook.filteredIdsCache";
const currentFilteredIds = ref(new Set());
const loadingFilteredSelectionState = ref(false);
const pendingAllChecked = ref(null);

const getActiveFilterState = () => compactFilters(props.filters || {});

const getFilterCacheKey = (filters = getActiveFilterState()) => JSON.stringify(filters);

const loadFilteredIdsCache = () => {
  try {
    const raw = localStorage.getItem(filteredIdsCacheStorageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const saveFilteredIdsCache = (cache) => {
  try {
    localStorage.setItem(filteredIdsCacheStorageKey, JSON.stringify(cache));
  } catch (e) {}
};

const getCachedFilteredIds = (filters = getActiveFilterState()) => {
  const cache = loadFilteredIdsCache();
  const entry = cache[getFilterCacheKey(filters)];
  if (!entry || !Array.isArray(entry.ids)) return null;

  const expectedTotal = Number(props.elements?.total ?? 0);
  const cachedTotal = Number(entry.total ?? entry.ids.length);

  if (expectedTotal !== cachedTotal) return null;

  return entry.ids;
};

const setCachedFilteredIds = (ids, filters = getActiveFilterState()) => {
  const cache = loadFilteredIdsCache();
  cache[getFilterCacheKey(filters)] = {
    total: Number(props.elements?.total ?? ids.length ?? 0),
    ids,
  };
  saveFilteredIdsCache(cache);
};

const fetchAllFilteredElements = async () => {
  const filters = compactFilters(filterValues.value);

  let page = 1;
  let lastPage = 1;
  const all = [];

  while (page <= lastPage) {
    const url = route("sales.lookbook.index", {
      filters,
      page,
      export: 1,
    });

    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error(`Export fetch failed (page ${page})`);

    const json = await res.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    all.push(...data);

    lastPage = Number(json?.last_page || 1);
    page += 1;
  }

  return all;
};

const fetchSelectedElements = async () => {
  const selectedIdsList = Array.from(selectedIds.value);

  let page = 1;
  let lastPage = 1;
  const all = [];

  while (page <= lastPage) {
    const url = route("sales.lookbook.index", {
      selected_ids: selectedIdsList,
      page,
      export: 1,
    });

    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error(`Selected export fetch failed (page ${page})`);

    const json = await res.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    all.push(...data);

    lastPage = Number(json?.last_page || 1);
    page += 1;
  }

  return all;
};

const fetchAllFilteredElementIds = async () => {
  const filters = getActiveFilterState();
  const url = route("sales.lookbook.index", {
    filters,
    ids_only: 1,
  });

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) throw new Error("Filtered ids fetch failed");

  const json = await res.json();
  return Array.isArray(json?.ids) ? json.ids : [];
};

const buildPdfFilename = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  return `lookbook_${stamp}.pdf`;
};

const buildLookbookPdfFile = async () => {
  const container = document.getElementById("lookbook-print");
  if (!container || !selectedIds.value.size) return null;

  exportItems.value = [];

  try {
    await nextTick();

    exportItems.value = await fetchSelectedElements();
    if (!exportItems.value.length) return;

    if (!exportItems.value.length) return;

    await nextTick();
    await nextTick();

    container.classList.add("exporting");
    await waitForBrowserPaint();
    await waitForImages(container);
    await waitForBrowserPaint();
    fitPrintPages(container);
    await waitForBrowserPaint();

    const pages = Array.from(container.querySelectorAll(".lookbook-page"));
    if (!pages.length) return;

    const pageWidthIn = 8.5;
    const pageHeightIn = 11;
    const marginIn = 0.5;
    const printWidthPx = 720;
    const printHeightPx = 960;

    const pdf = new jsPDF({
      unit: "in",
      format: "letter",
      orientation: "portrait",
    });

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: printWidthPx,
        height: printHeightPx,
        windowWidth: printWidthPx,
        windowHeight: printHeightPx,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      if (i > 0) pdf.addPage("letter", "portrait");

      pdf.addImage(
        imgData,
        "JPEG",
        marginIn,
        marginIn,
        pageWidthIn - marginIn * 2,
        pageHeightIn - marginIn * 2
      );
    }

    const filename = buildPdfFilename();
    const blob = pdf.output("blob");
    return new File([blob], filename, { type: "application/pdf" });
  } finally {
    container.classList.remove("exporting");
    exportItems.value = [];
  }
};

const generateLookbookPdfAllPages = async () => {
  if (!canDownloadLookbookPdf.value) return;

  exportingAllPdfButton.value = true;
  exportingAllPdfOverlay.value = true;

  try {
    const pdfFile = await buildLookbookPdfFile();
    if (!pdfFile) return;

    const objectUrl = URL.createObjectURL(pdfFile);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = pdfFile.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } finally {
    exportingAllPdfOverlay.value = false;
    exportingAllPdfButton.value = false;
  }
};

// -------------------- Selection (checkboxes) --------------------
const selectedIds = ref(new Set());
const allChecked = computed(() => {
  if (pendingAllChecked.value !== null) return pendingAllChecked.value;
  if (!currentFilteredIds.value.size) return false;
  return Array.from(currentFilteredIds.value).every((id) => selectedIds.value.has(id));
});

const isChecked = (id) => selectedIds.value.has(id);

const totalFilteredCount = computed(() => Number(props.elements?.total ?? props.elements?.data?.length ?? 0));

const selectedCount = computed(() => selectedIds.value.size);
const maxLookbookEmailPdfBytes = 5 * 1024 * 1024;
const lookbookEmailPdfValidationRun = ref(0);
const lookbookEmailPdfState = ref({
  checking: false,
  tooLarge: false,
  sizeBytes: null,
  key: null,
});
const cachedLookbookEmailPdf = ref({
  key: null,
  file: null,
});

const buildLookbookEmailPdfKey = () =>
  JSON.stringify({
    selectedIds: Array.from(selectedIds.value)
      .map((id) => Number(id))
      .sort((a, b) => a - b),
    coverId: selectedCoverId.value === null || selectedCoverId.value === undefined
      ? null
      : Number(selectedCoverId.value),
  });

const resetLookbookEmailPdfState = () => {
  lookbookEmailPdfState.value = {
    checking: false,
    tooLarge: false,
    sizeBytes: null,
    key: null,
  };
  cachedLookbookEmailPdf.value = {
    key: null,
    file: null,
  };
};

const validateLookbookEmailPdfSize = async () => {
  if (!canManageLookbook.value || !selectedIds.value.size) {
    resetLookbookEmailPdfState();
    return;
  }

  const key = buildLookbookEmailPdfKey();
  const runId = ++lookbookEmailPdfValidationRun.value;

  lookbookEmailPdfState.value = {
    checking: true,
    tooLarge: false,
    sizeBytes: null,
    key,
  };
  cachedLookbookEmailPdf.value = {
    key: null,
    file: null,
  };

  try {
    const pdfFile = await buildLookbookPdfFile();
    if (runId !== lookbookEmailPdfValidationRun.value || key !== buildLookbookEmailPdfKey()) return;

    const sizeBytes = Number(pdfFile?.size ?? 0);
    const tooLarge = !pdfFile || sizeBytes > maxLookbookEmailPdfBytes;

    lookbookEmailPdfState.value = {
      checking: false,
      tooLarge,
      sizeBytes,
      key,
    };
    cachedLookbookEmailPdf.value = tooLarge || !pdfFile
      ? { key: null, file: null }
      : { key, file: pdfFile };
  } catch (e) {
    if (runId !== lookbookEmailPdfValidationRun.value || key !== buildLookbookEmailPdfKey()) return;
    resetLookbookEmailPdfState();
  }
};

const debouncedValidateLookbookEmailPdfSize = _.debounce(validateLookbookEmailPdfSize, 500);
const isLookbookEmailDisabled = computed(
  () => !selectedCount.value || lookbookEmailPdfState.value.checking || lookbookEmailPdfState.value.tooLarge
);
const lookbookEmailDisabledReason = computed(() => {
  if (!selectedCount.value) return "";
  if (lookbookEmailPdfState.value.checking) {
    return "Checking generated PDF size for email.";
  }
  if (lookbookEmailPdfState.value.tooLarge) {
    return "Email sending is disabled because the generated PDF is larger than 5 MB.";
  }
  return "";
});

const saveSelectionToStorage = () => {
  try {
    localStorage.setItem(selectionStorageKey, JSON.stringify(Array.from(selectedIds.value)));
  } catch (e) {}
};

const loadSelectionFromStorage = () => {
  try {
    const raw = localStorage.getItem(selectionStorageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    const ids = Array.isArray(parsed) ? parsed : [];
    selectedIds.value = new Set(ids);
  } catch (e) {
    selectedIds.value = new Set();
  }
};

const refreshCurrentFilteredIds = async () => {
  if (!canDownloadLookbookPdf.value) return;

  const cachedIds = getCachedFilteredIds();
  if (cachedIds) {
    currentFilteredIds.value = new Set(cachedIds);
    pendingAllChecked.value = null;
    return;
  }

  loadingFilteredSelectionState.value = true;

  try {
    const ids = await fetchAllFilteredElementIds();
    currentFilteredIds.value = new Set(ids);
    setCachedFilteredIds(ids);
  } finally {
    loadingFilteredSelectionState.value = false;
    pendingAllChecked.value = null;
  }
};

const toggleOne = (id) => {
  pendingAllChecked.value = null;
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);

  selectedIds.value = next;
  saveSelectionToStorage();
};

const toggleAllVisible = async (checked) => {
  pendingAllChecked.value = checked;

  if (!totalFilteredCount.value) {
    currentFilteredIds.value = new Set();
    pendingAllChecked.value = false;
    return;
  }

  try {
    if (!currentFilteredIds.value.size || currentFilteredIds.value.size !== totalFilteredCount.value) {
      await refreshCurrentFilteredIds();
      pendingAllChecked.value = checked;
    }

    const next = new Set(selectedIds.value);

    if (checked) {
      currentFilteredIds.value.forEach((id) => next.add(id));
    } else {
      currentFilteredIds.value.forEach((id) => next.delete(id));
    }

    selectedIds.value = next;
    saveSelectionToStorage();
  } finally {
    pendingAllChecked.value = null;
  }
};

const resetLookbookPdfSelection = () => {
  selectedIds.value = new Set();
  pendingAllChecked.value = null;
  saveSelectionToStorage();
};

const sendingLookbookEmail = ref({ show: false, sending: false, error: null });
const sendLookbookEmailForm = useForm({
  email: "",
  subject: "",
  message: "",
  selected_ids: [],
  pdf: null,
});

const openLookbookEmailModal = () => {
  if (isLookbookEmailDisabled.value) return;

  sendingLookbookEmail.value = { show: true, sending: false, error: null };
  sendLookbookEmailForm.clearErrors();
  sendLookbookEmailForm.email = "";
  sendLookbookEmailForm.message = "";
  sendLookbookEmailForm.subject = `Lookbook PDF - ${new Date().toLocaleDateString("en-US")}`;
  sendLookbookEmailForm.selected_ids = Array.from(selectedIds.value);
  sendLookbookEmailForm.pdf = null;
};

const closeLookbookEmailModal = () => {
  sendingLookbookEmail.value = { show: false, sending: false, error: null };
  sendLookbookEmailForm.reset();
  sendLookbookEmailForm.clearErrors();
};

const sendLookbookEmail = () => {
  sendingLookbookEmail.value.error = null;

  sendLookbookEmailForm.selected_ids = Array.from(selectedIds.value);

  if (!sendLookbookEmailForm.selected_ids.length) {
    sendingLookbookEmail.value.error = "Select at least one lookbook element.";
    return;
  }

  if (!sendLookbookEmailForm.email) {
    sendingLookbookEmail.value.error = "Recipient is required.";
    return;
  }

  if (!sendLookbookEmailForm.subject) {
    sendingLookbookEmail.value.error = "Subject is required.";
    return;
  }

  if (lookbookEmailPdfState.value.tooLarge) {
    sendingLookbookEmail.value.error = "Email sending is disabled because the generated PDF is larger than 5 MB.";
    return;
  }

  sendingLookbookEmail.value.sending = true;

  const currentPdfKey = buildLookbookEmailPdfKey();
  const pdfPromise =
    cachedLookbookEmailPdf.value.key === currentPdfKey && cachedLookbookEmailPdf.value.file
      ? Promise.resolve(cachedLookbookEmailPdf.value.file)
      : buildLookbookPdfFile();

  pdfPromise
    .then((pdfFile) => {
      if (!pdfFile) {
        throw new Error("Could not generate lookbook PDF.");
      }

      if (pdfFile.size > maxLookbookEmailPdfBytes) {
        lookbookEmailPdfState.value = {
          checking: false,
          tooLarge: true,
          sizeBytes: pdfFile.size,
          key: currentPdfKey,
        };
        cachedLookbookEmailPdf.value = { key: null, file: null };
        throw new Error("Email sending is disabled because the generated PDF is larger than 5 MB.");
      }

      lookbookEmailPdfState.value = {
        checking: false,
        tooLarge: false,
        sizeBytes: pdfFile.size,
        key: currentPdfKey,
      };
      cachedLookbookEmailPdf.value = { key: currentPdfKey, file: pdfFile };
      sendLookbookEmailForm.pdf = pdfFile;

      sendLookbookEmailForm.post(route("sales.lookbook.sendEmail"), {
        preserveScroll: true,
        forceFormData: true,
        onSuccess: () => {
          sendingLookbookEmail.value.sending = false;
          closeLookbookEmailModal();
        },
        onError: () => {
          sendingLookbookEmail.value.sending = false;
          sendingLookbookEmail.value.error = "Email could not be sent.";
        },
        onFinish: () => {
          sendingLookbookEmail.value.sending = false;
          sendLookbookEmailForm.pdf = null;
        },
      });
    })
    .catch((error) => {
      sendingLookbookEmail.value.sending = false;
      sendingLookbookEmail.value.error =
        error?.message === "Email sending is disabled because the generated PDF is larger than 5 MB."
          ? error.message
          : "Could not generate the PDF to send.";
    });
};

loadSelectionFromStorage();

watch([selectedIds, selectedCoverId, canManageLookbook], () => {
  cachedLookbookEmailPdf.value = { key: null, file: null };

  if (!canManageLookbook.value || !selectedIds.value.size) {
    resetLookbookEmailPdfState();
    debouncedValidateLookbookEmailPdfSize.cancel();
    return;
  }

  debouncedValidateLookbookEmailPdfSize();
}, { immediate: true });

watch(
  () => props.filters,
  () => {
    loadSelectionFromStorage();
    void refreshCurrentFilteredIds();
  },
  { immediate: true }
);
</script>

<template>
  <AppLayout title="Lookbook">
    <LoadingOverlay
      v-model="exportingAllPdfOverlay"
      title="Exporting…"
      message="Your download will start shortly. Click to dismiss."
      :autoCloseOnFocus="true"
      :fallbackMs="45000"
    />

    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">Lookbook</h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-4 space-y-4">
        <div class="flex flex-wrap gap-2">
          <a v-if="canManageLookbook" :href="route('sales.lookbook.create')">
            <PrimaryButton>Add Lookbook Element</PrimaryButton>
          </a>

          <PrimaryButton
            v-if="canDownloadLookbookPdf"
            type="button"
            @click="generateLookbookPdfAllPages"
            :disabled="exportingAllPdfButton || !selectedCount"
          >
            {{ exportingAllPdfButton ? "Creating..." : "Create Lookbook PDF" }}
          </PrimaryButton>

          <PrimaryButton
            v-if="canDownloadLookbookPdf"
            type="button"
            @click="resetLookbookPdfSelection"
            :disabled="!selectedCount"
          >
            Reset Lookbook PDF
          </PrimaryButton>

          <span
            v-if="canManageLookbook"
            class="inline-flex quick-tooltip"
            :class="{ 'has-tooltip': !!lookbookEmailDisabledReason }"
            :data-tooltip="lookbookEmailDisabledReason || null"
          >
            <PrimaryButton
              type="button"
              @click="openLookbookEmailModal"
              :disabled="isLookbookEmailDisabled"
              :class="{ 'pointer-events-none': isLookbookEmailDisabled }"
            >
              {{
                lookbookEmailPdfState.checking
                  ? "Checking Email PDF..."
                  : "Send Lookbook by Email"
              }}
            </PrimaryButton>
          </span>
        </div>

        <div v-if="canDownloadLookbookPdf" class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <SecondaryButton @click="showCoversSection = !showCoversSection">
            {{ showCoversSection ? "Hide Covers" : "View Covers" }}
          </SecondaryButton>

          <SelectInput
            v-model="selectedCoverId"
            class="!w-56 max-w-full shrink-0 px-4 py-2 font-semibold text-xs uppercase tracking-widest text-gray-700"
          >
            <option :value="null">Default cover</option>
            <option v-for="cover in coverLibrary" :key="cover.id" :value="cover.id">
              {{ cover.title || `Cover #${cover.id}` }}
            </option>
          </SelectInput>

          <a
            v-if="canManageLookbook && showCoversSection"
            :href="route('sales.lookbook.covers.create', { return_to: currentIndexUrl })"
          >
            <PrimaryButton type="button">Add Cover</PrimaryButton>
          </a>
        </div>

        <div v-if="showCoversSection">
          <table class="min-w-full w-full text-left text-sm">
            <thead class="border-b">
              <tr>
                <th class="px-4 py-2">Client Logo</th>
                <th class="px-4 py-2">Cover</th>
                <th class="px-4 py-2">Title</th>
                <th class="px-4 py-2">Subtitle</th>
                <th class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cover in covers.data" :key="cover.id" class="border-b align-top">
                <td class="px-4 py-2">
                  <img
                    v-if="cover.client_logo_url"
                    :src="cover.client_logo_url"
                    :alt="`${cover.title || `Cover ${cover.id}`} client logo`"
                    class="h-14 max-w-[7rem] object-contain rounded cursor-pointer"
                    @click="openCoverPreview(cover)"
                  />
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="px-4 py-2">
                  <img
                    v-if="cover.cover_image_url"
                    :src="cover.cover_image_url"
                    :alt="cover.title || `Cover ${cover.id}`"
                    class="h-14 w-14 object-cover rounded cursor-pointer"
                    @click="openCoverPreview(cover)"
                  />
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="px-4 py-2">{{ cover.title || "—" }}</td>
                <td class="px-4 py-2">{{ cover.subtitle || "—" }}</td>
                <td class="px-4 py-2">
                  <div class="flex items-center gap-3 whitespace-nowrap">
                    <button
                      type="button"
                      class="fa fa-eye hover:text-gray-900"
                      title="Preview"
                      @click="openCoverPreview(cover)"
                    />
                    <a
                      v-if="canManageLookbook"
                      :href="
                        route('sales.lookbook.covers.edit', {
                          lookbookCover: cover.id,
                          return_to: currentIndexUrl,
                        })
                      "
                      class="fa fa-pen hover:text-gray-900"
                      title="Edit"
                    />
                    <button
                      type="button"
                      class="fa fa-file-pdf hover:text-gray-900"
                      title="Download preview"
                      @click="downloadCoverPreviewPdf(cover)"
                    />
                    <button
                      v-if="canManageLookbook"
                      type="button"
                      class="fa fa-trash hover:text-red-800"
                      title="Delete"
                      @click="deleteCover(cover)"
                    />
                  </div>
                </td>
              </tr>
              <tr v-if="!covers?.data?.length">
                <td colspan="5" class="px-4 py-4 text-sm text-gray-500">
                  No custom covers yet.
                </td>
              </tr>
            </tbody>
          </table>

          <div class="mt-4 px-4 pb-4">
            <PaginationLinks :pagination="covers" />
          </div>
        </div>

        <div class="grid gap-2 lg:grid-cols-4 md:grid-cols-2 grid-cols-1 w-full">
          <LookbookIndexFilters
            :filterValues="filterValues"
            :clients="clients"
            :departments="departments"
            :garments="garments"
            :fabrics="fabrics"
            :colors="colors"
            :canSeeNotes="canSeeNotes"
            :searching="searching"
            @search="makeSearchRequest"
            @reset="resetFilters"
          />
        </div>

        <div v-if="canDownloadLookbookPdf" class="text-sm text-gray-600">
          Selected: {{ selectedCount }} / {{ totalElementsCount || 0 }}
        </div>
      </div>

      <table class="min-w-full w-full text-left text-sm">
        <thead class="border-b">
          <tr>
            <th v-if="canDownloadLookbookPdf" class="px-4 py-2 w-12">
              <input
                type="checkbox"
                :checked="allChecked"
                @change="toggleAllVisible($event.target.checked)"
                :disabled="loadingFilteredSelectionState"
                title="Select all filtered elements"
              />
            </th>
            <th class="px-4 py-2 w-24 min-w-[6rem] max-w-[6rem]">Image</th>
            <th class="px-4 py-2">Project</th>
            <th class="px-4 py-2">Department</th>
            <th class="px-4 py-2">Garment</th>
            <th class="px-4 py-2">Project Description</th>
            <th v-if="canSeeNotes" class="px-4 py-2">Notes</th>
            <th class="px-4 py-2">Material</th>
            <th class="px-4 py-2">Color</th>
            <th class="px-4 py-2">Client</th>
            <th class="px-4 py-2">Date Updated</th> 
            <th class="px-4 py-2">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="e in elements.data" :key="e.id" class="border-b align-top">
            <td v-if="canDownloadLookbookPdf" class="px-4 py-2">
              <input
                type="checkbox"
                :checked="isChecked(e.id)"
                @change="toggleOne(e.id)"
                :aria-label="`Select ${e.project_name || 'element'}`"
              />
            </td>

            <td class="px-4 py-2 w-28 min-w-[6rem] max-w-[6rem]">
              <img
                v-if="e.url"
                :src="e.url"
                class="w-full h-auto rounded cursor-pointer"
                :alt="e.file_name"
                @click="openPreview(e)"
              />
              <span v-else class="text-gray-400">—</span>
            </td>

            <td class="px-4 py-2">
              <div class="font-medium">{{ e.project_name || "—" }}</div>
            </td>

            <td class="px-4 py-2">
              <span v-if="e.deptName">{{ e.deptName }}</span>
              <span v-else class="text-gray-500">{{
                e.department ? `#${e.department}` : "—"
              }}</span>
            </td>

            <td class="px-4 py-2">
              <span v-if="e.garment_model?.garmentName">{{
                e.garment_model.garmentName
              }}</span>
              <span v-else class="text-gray-500">{{ e.garment ? `#${e.garment}` : "—" }}</span>
            </td>

            <td class="px-4 py-2">
              <div class="whitespace-pre-line text-gray-700">
                {{ e.project_description || "—" }}
              </div>
            </td>

            <td v-if="canSeeNotes" class="px-4 py-2">
              <div class="whitespace-pre-line text-gray-700">
                {{ e.notes || "—" }}
              </div>
            </td>

            <td class="px-4 py-2">
              <span v-if="e.fabric_model?.fabName">{{ e.fabric_model.fabName }}</span>
              <span v-else class="text-gray-500">{{ e.fabric ? `#${e.fabric}` : "—" }}</span>
            </td>

            <td class="px-4 py-2">
              <span v-if="e.color_model?.colorName">{{ e.color_model.colorName }}</span>
              <span v-else class="text-gray-500">{{ e.color_id ? `#${e.color_id}` : "—" }}</span>
            </td>

            <td class="px-4 py-2">
              <span v-if="e.company?.client">{{ e.company.client }}</span>
              <span v-else class="text-gray-500">{{ e.client ? `#${e.client}` : "—" }}</span>
            </td>

            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatElementUpdatedAt(e.updated_at) }}
            </td>

            <td class="px-4 py-2">
              <div class="flex items-center gap-3 whitespace-nowrap">
                <button
                  type="button"
                  class="fa fa-eye hover:text-gray-900"
                  title="Preview"
                  @click="openPreview(e)"
                />

                <a
                  v-if="canManageLookbook"
                  :href="route('sales.lookbook.edit', e.id)"
                  class="fa fa-pen"
                  title="Edit"
                />

                <button
                  v-if="canManageLookbook"
                  type="button"
                  class="fa fa-copy hover:text-gray-900"
                  title="Copy"
                  @click="copyElement(e)"
                />

                <button
                  v-if="canDeleteLookbookElements"
                  type="button"
                  class="fa fa-trash hover:text-red-800"
                  title="Delete"
                  @click="confirmDelete(e)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-4">
        <PaginationLinks :pagination="elements" />
      </div>
    </div>

    <!-- ========================= -->
    <!--       PRINT: ALL PDF      -->
    <!-- ========================= -->
    <div id="lookbook-print">
      <!-- Cover -->
      <div class="lookbook-page cover-page">
        <div v-if="selectedCover" class="cover-wrapper custom-cover-layout">
          <img
            v-if="selectedCover.client_logo_url"
            :src="selectedCover.client_logo_url"
            class="custom-cover-client-logo"
            alt="Client logo"
          />

          <div class="custom-cover-image-slot">
            <img
              v-if="selectedCover?.cover_image_url"
              :src="selectedCover.cover_image_url"
              class="custom-cover-photo"
              alt="Cover"
            />
            <div v-else class="cover-no-image custom-cover-no-image">No cover image</div>
          </div>

          <div class="custom-cover-copy">
            <div v-if="coverTitle(selectedCover)" class="cover-title">{{ coverTitle(selectedCover) }}</div>
            <div v-if="selectedCover?.subtitle" class="cover-subtitle">
              {{ selectedCover.subtitle }}
            </div>
            <div v-if="selectedCover?.paragraph" class="cover-paragraph">
              {{ selectedCover.paragraph }}
            </div>
          </div>

          <img :src="logoUrl" class="custom-cover-premier-logo" :alt="brandDisplayName" />

          <div class="cover-disclaimer">
            {{ coverDisclaimer }}
          </div>
        </div>
        <div v-else class="cover-wrapper default-cover-layout">
          <img :src="logoUrl" class="default-cover-top-logo" :alt="brandDisplayName" />
          <img :src="defaultCoverImageUrl" class="default-cover-hero-image" :alt="`${brandDisplayName} locations`" />

          <div class="default-cover-content">
            <div class="default-cover-title">{{ lookbookTitle }}</div>
            <div class="default-cover-group">
              <div class="default-cover-text">Presented By</div>
              <div class="default-cover-text">{{ brandDisplayName }}</div>
              <div v-if="lookbookWebsite" class="default-cover-text">{{ lookbookWebsite }}</div>
            </div>
            <div v-if="lookbookPhone || lookbookEmail" class="default-cover-group default-cover-group-spaced">
              <div class="default-cover-text">For more information contact</div>
              <div v-if="lookbookPhone" class="default-cover-text">{{ lookbookPhone }}</div>
              <div v-if="lookbookEmail" class="default-cover-text">{{ lookbookEmail }}</div>
            </div>
            <div v-if="lookbookLocations" class="default-cover-locations">
              <div class="default-cover-text">Locations:</div>
              <span>{{ lookbookLocations }}</span>
            </div>
          </div>

          <div class="cover-disclaimer">
            {{ coverDisclaimer }}
          </div>
        </div>
      </div>

      <!-- Pages -->
      <div v-for="e in exportItems" :key="'print-' + e.id" class="lookbook-page">
        <div class="page-header">
          <img :src="logoUrl" class="page-logo" alt="Logo" />
        </div>

        <div class="print-wrapper">
          <div class="print-title">
            <div class="text-3xl font-bold text-gray-900 leading-tight text-center">
              {{ e.project_name || "Lookbook Element" }}
            </div>
          </div>

          <div class="print-image-wrapper">
            <img v-if="e.url" :src="e.url" :alt="e.file_name" class="print-image" />
            <div v-else class="print-no-image">No image</div>
          </div>

          <div class="print-info">
            <div class="print-info-primary">{{ primaryInfoLine(e) }}</div>
            <div class="print-info-body whitespace-pre-line">{{ safe(e.project_description) }}</div>
            <div class="print-info-secondary">{{ secondaryInfoLine(e) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================= -->
    <!--     PRINT: PREVIEW PDF    -->
    <!-- ========================= -->
    <div id="lookbook-preview-print">
      <div v-if="previewExportItem" class="lookbook-page">
        <div class="page-header">
          <img :src="logoUrl" class="page-logo" alt="Logo" />
        </div>

        <div class="print-wrapper">
          <div class="print-title">
            <div class="text-3xl font-bold text-gray-900 leading-tight text-center">
              {{ previewExportItem.project_name || "Lookbook Element" }}
            </div>
          </div>

          <div class="print-image-wrapper">
            <img
              v-if="previewExportItem.url"
              :src="previewExportItem.url"
              :alt="previewExportItem.file_name"
              class="print-image"
            />
            <div v-else class="print-no-image">No image</div>
          </div>

          <div class="print-info">
            <div class="print-info-primary">{{ primaryInfoLine(previewExportItem) }}</div>
            <div class="print-info-body whitespace-pre-line">{{ safe(previewExportItem.project_description) }}</div>
            <div class="print-info-secondary">{{ secondaryInfoLine(previewExportItem) }}</div>
          </div>
        </div>

        <div
          v-if="canViewOnlyLookbook"
          class="print-watermark-layer"
        >
          <img :src="printWatermarkUrl" alt="Premier watermark" class="watermark-center-logo" />
        </div>
      </div>
    </div>

    <!-- ========================= -->
    <!--   PRINT: COVER PREVIEW    -->
    <!-- ========================= -->
    <div id="lookbook-cover-preview-print">
      <div v-if="coverPreviewItem" class="lookbook-page cover-page">
        <div class="cover-wrapper custom-cover-layout">
          <img
            v-if="coverPreviewItem.client_logo_url"
            :src="coverPreviewItem.client_logo_url"
            class="custom-cover-client-logo"
            alt="Client logo"
          />

          <div class="custom-cover-image-slot">
            <img
              v-if="coverPreviewItem.cover_image_url"
              :src="coverPreviewItem.cover_image_url"
              class="custom-cover-photo"
              alt="Cover"
            />
            <div v-else class="cover-no-image custom-cover-no-image">No cover image</div>
          </div>

          <div class="custom-cover-copy">
            <div v-if="coverTitle(coverPreviewItem)" class="cover-title">{{ coverTitle(coverPreviewItem) }}</div>
            <div v-if="coverPreviewItem.subtitle" class="cover-subtitle">
              {{ coverPreviewItem.subtitle }}
            </div>
            <div v-if="coverPreviewItem.paragraph" class="cover-paragraph">
              {{ coverPreviewItem.paragraph }}
            </div>
          </div>

          <img :src="logoUrl" class="custom-cover-premier-logo" :alt="brandDisplayName" />

          <div class="cover-disclaimer">
            {{ coverDisclaimer }}
          </div>
        </div>
      </div>
    </div>

    <!-- ========================= -->
    <!--         PREVIEW UI        -->
    <!-- ========================= -->
    <DialogModal :show="showPreviewModal" @close="closePreview" max-width="4xl">
      <template #title>Preview</template>

      <template #content>
        <div v-if="elementToPreview" class="bg-white">
          <div class="px-6 py-6 preview-content-wrapper">
            <div class="text-center">
              <div class="text-3xl font-semibold text-gray-900 leading-tight">
                {{ previewTitleLine(elementToPreview) }}
              </div>
            </div>

            <div class="mt-6 flex justify-center">
              <div class="w-full max-w-md">
                <img
                  v-if="elementToPreview.url"
                  :src="elementToPreview.url"
                  :alt="elementToPreview.file_name"
                  class="w-full h-auto object-contain"
                />
                <div
                  v-else
                  class="w-full h-64 border rounded flex items-center justify-center text-gray-400"
                >
                  No image
                </div>
              </div>
            </div>

            <div class="mt-8 text-center text-base text-gray-900 space-y-1 font-bold">
              <div class="mt-3">
                <div class="text-lg">{{ primaryInfoLine(elementToPreview) }}</div>
                <div class="mt-3 whitespace-pre-line">{{ safe(elementToPreview.project_description) }}</div>
                <div class="mt-3">{{ secondaryInfoLine(elementToPreview) }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="w-full flex justify-center">
          <div class="flex flex-wrap justify-center gap-3">
            <PrimaryButton
              type="button"
              @click="downloadPdfFromPreview"
              :disabled="!elementToPreview?.id || exportingPreviewPdf"
            >
              {{ exportingPreviewPdf ? "Generating..." : "Download PDF" }}
            </PrimaryButton>

            <PrimaryButton
              type="button"
              @click="downloadImageFromPreview"
              :disabled="!elementToPreview?.url"
            >
              Download image
            </PrimaryButton>

            <PrimaryButton
              v-if="canManageLookbook"
              type="button"
              @click="goEditFromPreview"
              :disabled="!elementToPreview?.id"
            >
              Edit
            </PrimaryButton>

            <PrimaryButton
              v-if="canManageLookbook"
              type="button"
              @click="copyElement(elementToPreview)"
              :disabled="!elementToPreview?.id"
            >
              Copy
            </PrimaryButton>

            <SecondaryButton @click="closePreview">Close</SecondaryButton>
          </div>
        </div>
      </template>
    </DialogModal>

    <DialogModal :show="showCoverPreviewModal" @close="closeCoverPreview" max-width="4xl">
      <template #title>Cover Preview</template>

      <template #content>
        <div v-if="coverToPreview" class="bg-white">
          <div class="cover-preview-box custom-cover-layout">
            <img
              v-if="coverToPreview.client_logo_url"
              :src="coverToPreview.client_logo_url"
              class="custom-cover-client-logo"
              alt="Client logo"
            />

            <div class="custom-cover-image-slot">
              <img
                v-if="coverToPreview.cover_image_url"
                :src="coverToPreview.cover_image_url"
                class="custom-cover-photo"
                alt="Cover"
              />
              <div v-else class="cover-no-image custom-cover-no-image">No cover image</div>
            </div>

            <div class="custom-cover-copy">
              <div v-if="coverTitle(coverToPreview)" class="cover-title">{{ coverTitle(coverToPreview) }}</div>
              <div v-if="coverToPreview.subtitle" class="cover-subtitle">
                {{ coverToPreview.subtitle }}
              </div>
              <div v-if="coverToPreview.paragraph" class="cover-paragraph">
                {{ coverToPreview.paragraph }}
              </div>
            </div>

            <img :src="logoUrl" class="custom-cover-premier-logo" :alt="brandDisplayName" />

            <div class="cover-disclaimer">
              {{ coverDisclaimer }}
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <PrimaryButton
          type="button"
          @click="downloadCoverPreviewPdf(coverToPreview)"
          :disabled="!coverToPreview?.id || exportingCoverPreviewPdf"
        >
          {{ exportingCoverPreviewPdf ? "Generating..." : "Download preview PDF" }}
        </PrimaryButton>
        <SecondaryButton class="ml-3" @click="closeCoverPreview">Close</SecondaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="sendingLookbookEmail.show" @close="closeLookbookEmailModal" max-width="2xl">
      <template #title>Send Lookbook Email</template>

      <template #content>
        <div class="space-y-4">
          <p v-if="sendingLookbookEmail.error" class="text-sm text-red-600">
            {{ sendingLookbookEmail.error }}
          </p>

          <div class="text-sm text-gray-600">
            Selected elements: {{ selectedCount }}
          </div>

          <div>
            <InputLabel for="lookbook-email" value="Recipient" required />
            <TextInput
              id="lookbook-email"
              v-model="sendLookbookEmailForm.email"
              type="email"
              class="mt-1 block w-full"
              :disabled="sendingLookbookEmail.sending"
            />
            <InputError :message="sendLookbookEmailForm.errors.email" class="mt-2" />
          </div>

          <div>
            <InputLabel for="lookbook-subject" value="Subject" required />
            <TextInput
              id="lookbook-subject"
              v-model="sendLookbookEmailForm.subject"
              type="text"
              class="mt-1 block w-full"
              :disabled="sendingLookbookEmail.sending"
            />
            <InputError :message="sendLookbookEmailForm.errors.subject" class="mt-2" />
          </div>

          <div>
            <InputLabel for="lookbook-message" value="Message" />
            <TextareaInput
              id="lookbook-message"
              v-model="sendLookbookEmailForm.message"
              rows="5"
              class="mt-1 block w-full"
              :disabled="sendingLookbookEmail.sending"
            />
            <InputError :message="sendLookbookEmailForm.errors.message" class="mt-2" />
          </div>

          <p class="text-xs text-gray-500">
            The email will include your message and attach the generated lookbook PDF with the current selected cover.
          </p>
          <InputError :message="sendLookbookEmailForm.errors.selected_ids" class="mt-2" />
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeLookbookEmailModal" :disabled="sendingLookbookEmail.sending">
          Cancel
        </SecondaryButton>

        <span
          class="ml-3 inline-flex quick-tooltip"
          :class="{ 'has-tooltip': !!lookbookEmailDisabledReason }"
          :data-tooltip="lookbookEmailDisabledReason || null"
        >
          <PrimaryButton
            type="button"
            @click="sendLookbookEmail"
            :disabled="sendingLookbookEmail.sending || lookbookEmailPdfState.checking || lookbookEmailPdfState.tooLarge"
            :class="{ 'pointer-events-none': sendingLookbookEmail.sending || lookbookEmailPdfState.checking || lookbookEmailPdfState.tooLarge }"
          >
          {{ sendingLookbookEmail.sending ? "Sending..." : "Send Email" }}
          </PrimaryButton>
        </span>
      </template>
    </DialogModal>

    <!-- Delete modal -->
    <ConfirmationModal :show="showDeleteModal" @close="closeDeleteModal">
      <template #title>Delete lookbook element</template>

      <template #content>
        <div class="text-sm text-gray-700">
          Are you sure you want to delete
          <span class="font-semibold">{{ elementToDelete?.project_name || "this element" }}</span
          >?
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeleteModal">Cancel</SecondaryButton>

        <button
          type="button"
          class="ml-3 inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700"
          @click="deleteElement"
        >
          Delete
        </button>
      </template>
    </ConfirmationModal>
  </AppLayout>
</template>

<style>
/* ============================= */
/*         PDF LAYOUT FIX        */
/* ============================= */

.lookbook-page {
  height: 10in;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  background: #fff;
  font-family: Arial, Helvetica, sans-serif;
}

#lookbook-print {
  position: fixed;
  top: 0;
  left: -9999px;
  width: 720px;
  z-index: -1;
  pointer-events: none;
  visibility: hidden;
}

#lookbook-print.exporting {
  visibility: visible;
  z-index: 9999;
}

#lookbook-preview-print {
  position: fixed;
  top: 0;
  left: -9999px;
  width: 720px;
  z-index: -1;
  pointer-events: none;
  visibility: hidden;
}

#lookbook-preview-print.exporting {
  visibility: visible;
  z-index: 9999;
}

#lookbook-cover-preview-print {
  position: fixed;
  top: 0;
  left: -9999px;
  width: 720px;
  z-index: -1;
  pointer-events: none;
  visibility: hidden;
}

#lookbook-cover-preview-print.exporting {
  visibility: visible;
  z-index: 9999;
}

.page-header {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 140;
}

.page-logo {
  width: 220px;
  height: auto;
}

/* ============================= */
/*       PRINT CONTENT LAYOUT    */
/* ============================= */

.print-wrapper {
  height: 100%;
  box-sizing: border-box;
  padding: 70px 32px 40px 32px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  row-gap: 20px;
  min-height: 0;
  position: relative;
  z-index: 10;
}

.print-title {
  flex: 0 0 auto;
  position: relative;
  z-index: 140;
}

.print-image-wrapper {
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 20;
}

.print-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  position: relative;
  z-index: 20;
}

.print-no-image {
  width: 100%;
  height: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.print-info {
  margin-top: 0;
  text-align: center;
  position: relative;
  z-index: 140;

  font-size: 15px;
  line-height: 1.6;
  letter-spacing: 0.2px;
  font-weight: 700;

  page-break-inside: avoid;
  break-inside: avoid;
}

.print-info-primary {
  font-size: 18px;
  margin-bottom: 10px;
}

.print-info-body {
  margin-bottom: 10px;
}

.print-info-secondary {
  margin-bottom: 0;
}

.preview-content-wrapper {
  position: relative;
}

.preview-content-wrapper > * {
  position: relative;
  z-index: 1;
}

.print-watermark-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
}

.watermark-center-logo {
  width: 72%;
  max-width: 560px;
  max-height: 72%;
  height: auto;
  object-fit: contain;
  opacity: 0.45;
  transform: rotate(-30deg);
}

/* ============================= */
/*         COVER PAGE            */
/* ============================= */

.cover-page {
  background: #ffffff;
}

.cover-wrapper {
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px;
  padding-top: 60px;
}

.cover-preview-box {
  min-height: 520px;
  background: #ffffff;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.cover-photo {
  width: 100%;
  max-width: 520px;
  max-height: 420px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 24px;
}

.cover-no-image {
  width: 100%;
  max-width: 520px;
  height: 260px;
  border: 1px solid #ddd;
  border-radius: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.cover-default-logo {
  width: 220px;
  height: auto;
  margin-bottom: 40px;
}

.default-cover-layout {
  width: 100%;
  justify-content: flex-start;
  padding: 42px 0 24px;
}

.default-cover-top-logo {
  width: 240px;
  max-width: 80%;
  height: auto;
  margin-bottom: 24px;
}

.default-cover-hero-image {
  align-self: stretch;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 0 24px;
}

.default-cover-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  color: #111;
  font-weight: 700;
  padding: 0 24px;
}

.default-cover-title {
  font-size: 30px;
  line-height: 1.05;
  margin-bottom: 10px;
}

.default-cover-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.default-cover-group-spaced {
  margin-top: 18px;
}

.default-cover-text,
.default-cover-locations {
  font-size: 14px;
  line-height: 1.15;
}

.default-cover-locations {
  margin-top: 24px;
}

.default-cover-locations span {
  display: block;
  margin-top: 6px;
}

.default-cover-layout .cover-disclaimer {
  margin-top: 20px;
  max-width: 1320px;
  font-size: 9px;
  line-height: 1.2;
  padding-left: 28px;
  padding-right: 28px;
}

.custom-cover-layout {
  width: 100%;
  justify-content: flex-start;
  padding: 36px 24px 24px;
  gap: 18px;
}

.custom-cover-client-logo {
  width: auto;
  max-width: 260px;
  max-height: 90px;
  object-fit: contain;
  flex: 0 0 auto;
}

.custom-cover-image-slot {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-cover-photo {
  width: auto;
  max-width: 100%;
  max-height: 100%;
  height: auto;
  display: block;
}

.custom-cover-no-image {
  width: 100%;
  max-width: none;
  height: 100%;
  min-height: 160px;
}

.custom-cover-copy {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}

.custom-cover-premier-logo {
  flex: 0 0 auto;
  width: auto;
  max-width: 240px;
  max-height: 78px;
  object-fit: contain;
}

.cover-inline-logo {
  max-width: 180px;
  max-height: 72px;
  width: auto;
  object-fit: contain;
}

.cover-inline-logo-premier {
  max-width: 190px;
}

.cover-inline-logo-client {
  max-width: 160px;
}

.cover-title {
  font-size: 48px;
  font-weight: 600;
  letter-spacing: 2px;
  color: #111;
}

.cover-subtitle {
  margin-top: 14px;
  font-size: 22px;
  font-weight: 500;
  color: #222;
}

.cover-paragraph {
  margin-top: 14px;
  max-width: 560px;
  white-space: pre-line;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.cover-date {
  margin-top: 18px;
  font-size: 14px;
  letter-spacing: 1px;
  color: #222;
}

.cover-confidential {
  margin-top: 28px;
  font-size: 14px;
  letter-spacing: 4px;
  color: #333;
  opacity: 0.7;
}

.cover-disclaimer {
  margin-top: 28px;
  padding-top: 0;
  max-width: 1180px;
  font-size: 11px;
  line-height: 1.28;
  color: #222;
  padding-left: 40px;
  padding-right: 40px;
}

.quick-tooltip {
  position: relative;
}

.quick-tooltip.has-tooltip::before,
.quick-tooltip.has-tooltip::after {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.08s ease-out, transform 0.08s ease-out;
}

.quick-tooltip.has-tooltip::before {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 10px);
  transform: translateX(-50%) translateY(2px);
  background: #111827;
  color: #fff;
  font-size: 12px;
  line-height: 1.2;
  padding: 8px 10px;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.18);
  z-index: 50;
}

.quick-tooltip.has-tooltip::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: calc(100% + 4px);
  transform: translateX(-50%) translateY(2px);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #111827;
  z-index: 50;
}

.quick-tooltip.has-tooltip:hover::before,
.quick-tooltip.has-tooltip:hover::after,
.quick-tooltip.has-tooltip:focus-within::before,
.quick-tooltip.has-tooltip:focus-within::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
</style>
